'use client';

import { type FC, type ReactNode, useState } from 'react';
import { ModalSheet, Button } from '../../../ui';
import { useLocalTranslation } from '../../../../hooks/useLocalTranslation';
import translations from '../RushMid.translations';
import { RpsDiagram } from './RpsDiagram';
import { SideIcon } from '../../SideIcon';
import { WEAPON_ICON_SRC } from '../../spriteLayout';
import { FaGift } from '../../../icons';
import { AtlasIcon } from '../shared/AtlasIcon';

interface FirstRunSlidesProps {
  isOpen: boolean;
  onClose: () => void;
  onDone: () => void;
}

const SlideArt: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex h-[220px] items-center justify-center">{children}</div>
);

const WeaponIcon: FC<{ src: string; size?: number }> = ({ src, size = 56 }) => (
  <AtlasIcon src={src} size={size} className="select-none" />
);

export const FirstRunSlides: FC<FirstRunSlidesProps> = ({ isOpen, onClose, onDone }) => {
  const { t } = useLocalTranslation(translations);
  const [i, setI] = useState(0);

  const slides: { title: string; text: string; art: ReactNode; hint?: string }[] = [
    {
      title: t('howtoSlide1Title'),
      text: t('howtoSlide1Text'),
      art: (
        <div className="flex items-center gap-6">
          <SideIcon side="ct" size={72} />
          <span className="text-2xl font-black text-white/60">VS</span>
          <SideIcon side="t" size={72} />
        </div>
      ),
    },
    {
      title: t('howtoSlide2Title'),
      text: t('howtoSlide2Text'),
      art: (
        <div className="flex items-center gap-5">
          <WeaponIcon src={WEAPON_ICON_SRC.awp} size={72} />
          <WeaponIcon src={WEAPON_ICON_SRC.ak} size={64} />
          <WeaponIcon src={WEAPON_ICON_SRC.lmg} size={64} />
        </div>
      ),
    },
    {
      title: t('howtoSlide3Title'),
      text: t('howtoSlide3Text'),
      art: <RpsDiagram compact />,
    },
    {
      title: t('howtoSlide4Title'),
      text: t('howtoSlide4Text'),
      art: <FaGift size={88} className="text-price drop-shadow-[0_0_24px_rgba(251,191,36,0.35)]" />,
      hint: t('howtoTryBots'),
    },
  ];

  const last = i === slides.length - 1;
  const s = slides[i];

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col px-5 pb-5 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="self-end text-sm text-label-secondary active:opacity-60"
        >
          {t('howtoSkip')}
        </button>

        <SlideArt>{s.art}</SlideArt>

        <h2 className="text-center text-xl font-bold text-white">{s.title}</h2>
        <p className="mt-2 min-h-[72px] text-center text-sm leading-snug text-white/85">{s.text}</p>
        {s.hint && <p className="text-center text-sm font-semibold text-accent-green">{s.hint}</p>}

        <div className="my-4 flex items-center justify-center gap-2">
          {slides.map((_, d) => (
            <button
              key={d}
              type="button"
              aria-label={`slide ${d + 1}`}
              onClick={() => setI(d)}
              className={`h-2 rounded-full transition-all ${d === i ? 'w-6 bg-accent-blue' : 'w-2 bg-white/20'}`}
            />
          ))}
        </div>

        <Button
          variant="primary"
          size="l"
          stretched
          data-sound="cta"
          onClick={() => (last ? onDone() : setI(i + 1))}
        >
          {last ? t('howtoStart') : t('howtoNext')}
        </Button>
      </div>
    </ModalSheet>
  );
};
